import { useRoleAuth } from '@src/hooks';
import {
  ACTION,
  BlotterDensity,
  DEFAULT_MAX_ROWS,
  DEFAULT_PAGINATION_SIZE,
  EntityAdmin,
  getTimezoneDrawerOptions,
  SCHEDULE,
  type ColumnDef,
  type FilterableProperty,
  type InputsAndDropdownsDrawerOption,
  type ISchedule,
} from '@talos/kyoko';
import { useMemo } from 'react';

const WS_REQUEST = { name: SCHEDULE, tag: 'CustomerSchedule' };

export const CustomerSchedule = () => {
  const { isAuthorized } = useRoleAuth();

  const columns: ColumnDef<ISchedule>[] = useMemo(
    () => [
      {
        type: 'text',
        field: 'ScheduleID',
        headerName: 'Schedule ID',
        initialWidth: 120,
      },
      {
        type: 'text',
        field: 'Name',
        headerName: 'Name',
        initialWidth: 200,
      },
      {
        type: 'text',
        field: 'StartTime',
        headerName: 'Start Time',
        initialWidth: 120,
      },
      {
        type: 'text',
        field: 'TimeZone',
        headerName: 'Time Zone',
        initialWidth: 120,
      },
      {
        type: 'text',
        field: 'Interval',
        headerName: 'Interval',
        initialWidth: 120,
      },
    ],
    []
  );

  const drawerOptions: InputsAndDropdownsDrawerOption<ISchedule>[] = useMemo(
    () => [
      {
        type: 'input',
        field: 'Name',
        title: 'Schedule Name',
        getIsRequired: () => true,
        placeholder: 'e.g., Daily Funding Rate Calculation',
      },
      {
        type: 'input',
        field: 'StartTime',
        title: 'Start Time',
        getIsRequired: () => true,
        placeholder: 'HH:MM:SS format (e.g., 08:00:00)',
      },
      {
        type: 'dropdown',
        field: 'TimeZone',
        title: 'Time Zone',
        getDropdownOptions: () => getTimezoneDrawerOptions(),
        placeholder: 'Select timezone...',
      },
      {
        type: 'input',
        field: 'Interval',
        title: 'Interval',
        getIsRequired: () => true,
        placeholder: 'e.g., 24h, 12h, 1d',
      },
    ],
    []
  );

  const filterableProperties: FilterableProperty[] | undefined = useMemo(() => {
    // For now, no filters - can be added later if needed
    return [];
  }, []);

  return (
    <EntityAdmin<ISchedule>
      title="Schedule Configuration"
      subtitle="View and manage schedule configurations."
      entityName="Schedule Configuration"
      path="/schedules"
      getPostPath={() => '/schedules'}
      getPatchPath={entity => `/schedules/${entity.ScheduleID}`}
      getDeletePath={entity => `/schedules/${entity.ScheduleID}`}
      entityIDField="ScheduleID"
      density={BlotterDensity.Comfortable}
      columns={columns}
      drawerOptions={drawerOptions}
      filterableProperties={filterableProperties}
      allowModeSwitch={false}
      allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowDeleteEntity={isAuthorized(ACTION.EDIT_DEALER)}
      persistKey="schedules"
      blotterRowCount={DEFAULT_MAX_ROWS}
      paginationSize={DEFAULT_PAGINATION_SIZE}
      allowBulkEdit={false}
      wsRequest={WS_REQUEST}
    />
  );
};
